import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Swiper from 'swiper';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { classnames } from 'helpers/utils/classnames';
import { desktop } from 'helpers/utils/screensizes';
import useTouchDevice from 'helpers/hooks/useTouchDevice';

interface GalleryProps {
  images: Array<ImageProps>;
  inModalVersion?: boolean;
}

const Gallery: FC<GalleryProps> = ({ images, inModalVersion }) => {
  const swiperRef = useRef<Swiper>();
  const [activeSlide, setActiveSlide] = useState(0);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isUserInteraction = useRef(false);

  const setThumbnailRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      thumbnailRefs.current[index] = el;
    },
    [],
  );

  const [isDesktopSize] = useMediaQuery(desktop);
  const { isTouchDevice } = useTouchDevice();

  const [isLoading, setIsLoading] = useState(true);

  const getNormalizedIndex = (index: number) => {
    const totalSlides = images.length;
    return ((index % totalSlides) + totalSlides) % totalSlides;
  };

  const slideTo = (index: number) => {
    if (!swiperRef.current) return;

    isUserInteraction.current = true;
    const normalizedIndex = getNormalizedIndex(index);
    swiperRef.current.slideToLoop(normalizedIndex);
    setActiveSlide(normalizedIndex);
  };

  const handleSlideChange = (swiper: Swiper) => {
    if (isUserInteraction.current) {
      isUserInteraction.current = false;
      return;
    }

    const newIndex = getNormalizedIndex(swiper.realIndex);
    setActiveSlide(newIndex);
  };

  useEffect(() => {
    const container = thumbnailContainerRef.current;
    const activeThumbnail = thumbnailRefs.current[activeSlide];

    if (container && activeThumbnail) {
      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      const containerLeft = containerRect.left;
      const containerRight = containerRect.right;
      const thumbnailLeft = thumbnailRect.left;
      const thumbnailRight = thumbnailRect.right;

      if (thumbnailLeft < containerLeft || thumbnailRight > containerRight) {
        const scrollAmount =
          thumbnailLeft - containerLeft + container.scrollLeft - containerRect.width / 2 + thumbnailRect.width / 2;

        container.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    }
  }, [activeSlide]);

  const containerClassName = useClassNames(['relative w-full', inModalVersion ? 'h-[250px]' : 'h-300 sm:h-[447px]']);
  const scrollbarClass = isTouchDevice ? 'scrollbar-hide' : 'pb-8';

  return (
    <div className="gap-y-34 md:mb-50">
      <div className={containerClassName}>
        <div className={isLoading ? 'block' : 'hidden'}>
          <Image
            src={images[0]?.src}
            alt={images[0]?.alt}
            media={images[0]?.media}
            loading="eager"
            /*
            TODO: Commenting this for now so images show up. We need to implement the right Cloudinary strat. eventually
            suffix="large"
            */
            style={{ objectFit: 'contain' }}
            fill
          />
        </div>

        <div className={isLoading ? 'hidden' : 'block'}>
          <Slider
            onSlideChange={handleSlideChange}
            onSwiper={(swiper: Swiper) => {
              swiperRef.current = swiper;
            }}
            arrows={isDesktopSize && images.length > 1}
            dots={!isDesktopSize && images.length > 1}
            prevButtonStyles={{ left: isDesktopSize ? -10 : -4 }}
            nextButtonStyles={{ right: isDesktopSize ? -10 : -4 }}
            compactNavigation={inModalVersion}
            slidesPerView={1}
            loop={images.length > 1}
            onInit={() => setIsLoading(false)}
          >
            {images?.map((image, index) => (
              <div
                key={index}
                className={classnames('relative w-full', inModalVersion ? 'h-250' : 'h-320 sm:h-[447px]')}
              >
                <Image
                  src={image?.src}
                  alt={image?.alt}
                  media={image?.media}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  /*
                  TODO: Commenting this for now so images show up. We need to implement the right Cloudinary strat. eventually
                  suffix="large"
                  */
                  style={{ objectFit: 'contain' }}
                  fill
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {!inModalVersion && (
        <div className="relative mt-20 flex justify-center px-16 md:justify-start">
          <div ref={thumbnailContainerRef} className={`overflow-x-auto scroll-smooth pb-4 ${scrollbarClass}`}>
            <div className="flex min-w-min gap-x-16">
              {images?.map((image, index) => (
                <div
                  key={index}
                  ref={setThumbnailRef(index)}
                  className={`relative size-64 flex-shrink-0 cursor-pointer border-2 p-1 transition-colors md:size-112 ${
                    index === activeSlide % images.length ? 'border-black' : 'border-neutral-black-200'
                  }`}
                  onClick={() => slideTo(index)}
                >
                  <div className="relative size-full">
                    <Image
                      src={image?.src}
                      alt={image?.alt}
                      media={image?.media}
                      /*
                      TODO: Commenting this for now so images show up. We need to implement the right Cloudinary strat. eventually
                      suffix="small"
                      */
                      style={{ objectFit: 'contain' }}
                      fill
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
